import { useCallback, useEffect, useRef, useState } from "react"
import { GridCellParams, GridRowId } from "@mui/x-data-grid-premium"

// Aligned with contributors-cell-value-flash animation duration in style.scss.
const FLASH_DURATION_MS = 1200

export interface FlashableRow {
  id: GridRowId
  contributor?: { values?: unknown }
}

export interface FieldExtractor {
  field: string
  getValue: (row: FlashableRow) => unknown
}

/**
 * Returns a getCellClassName callback that briefly highlights only the metric
 * cells whose individual values changed since the last render. Skips comparison
 * while `loading` to avoid spurious flashes on the initial data paint.
 *
 * Callers supply `fieldExtractors` — one per metric column — so the hook
 * compares each field's value independently rather than the entire
 * contributor.values object. Only cells whose extracted value actually changed
 * receive the `value-updated` class.
 *
 * The clear timer is stored in a ref so that unrelated re-renders (e.g. MUI
 * column virtualisation) cannot cancel an in-flight flash.
 */
export function useCellValueFlash(
  rows: FlashableRow[],
  loading: boolean,
  fieldExtractors: FieldExtractor[],
): (params: GridCellParams) => string {
  const prevFieldValuesRef = useRef<Map<GridRowId, Map<string, unknown>> | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [flashingCells, setFlashingCells] = useState<Map<GridRowId, Set<string>>>(() => new Map())

  useEffect(() => {
    const prev = prevFieldValuesRef.current

    const curr = new Map<GridRowId, Map<string, unknown>>(
      rows.map(row => [
        row.id,
        new Map(fieldExtractors.map(({ field, getValue }) => [field, getValue(row)])),
      ]),
    )
    if (loading) return

    prevFieldValuesRef.current = curr

    if (!prev) return

    const changed = new Map<GridRowId, Set<string>>()
    for (const [rowId, currFields] of curr) {
      const prevFields = prev.get(rowId)
      if (!prevFields) { continue }

      const changedFields = new Set<string>()
      for (const [field, currValue] of currFields) {
        if (!prevFields.has(field)) { continue }
        if (!Object.is(currValue, prevFields.get(field))) { changedFields.add(field) }
      }
      if (changedFields.size > 0) changed.set(rowId, changedFields)
    }

    if (changed.size === 0) return

    clearTimeout(timerRef.current ?? undefined)
    timerRef.current = setTimeout(() => {
      setFlashingCells(changed)
      timerRef.current = setTimeout(() => setFlashingCells(new Map()), FLASH_DURATION_MS)
    }, 0)
  }, [rows, loading, fieldExtractors])

  useEffect(() => () => clearTimeout(timerRef.current ?? undefined), [])

  return useCallback(
    (params: GridCellParams) => {
      if (!flashingCells.size) return ""
      return flashingCells.get(params.id)?.has(params.field) ? "value-updated" : ""
    },
    [flashingCells],
  )
}
